// One-time dismissible banner on the Today screen prompting the user to
// connect a wearable. Hidden permanently after dismiss (localStorage flag)
// and also auto-hidden once any wearable connection exists.
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listWearableConnections } from "@/lib/wearables.functions";
import { SignInForWearablesBanner } from "@/knowmeno/SignInForWearablesBanner";
import { classifyWearableError } from "@/knowmeno/wearableErrors";


const PL = "#4B1F4F";
const GR = "#6E4B71";
const WH = "#ffffff";
const TL = "#FAF1F4";
const GL = "#FAF7F5";

const DISMISS_KEY = "knowmeno_wearable_banner_dismissed";

function getIsTestUser(): boolean {
  try {
    const raw = window.localStorage.getItem("menotrack_user");
    if (!raw) return false;
    const u = JSON.parse(raw);
    return u.isTestUser === true;
  } catch {
    return false;
  }
}

export function WearableBanner({ onOpenSettings }: { onOpenSettings: () => void }) {
  const list = useServerFn(listWearableConnections);
  const [visible, setVisible] = useState(false);
  const [authMissing, setAuthMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      return;
    }
    (async () => {
      try {
        const { showAuthBanner } = classifyWearableError(undefined);
        if (showAuthBanner) { if (!cancelled) { setAuthMissing(true); setVisible(true); } return; }
        const res = await list();
        const hasAny = (res?.connections ?? []).some(
          (c: any) => c.status === "connected" || c.status === "pending",
        );
        if (!cancelled && !hasAny) setVisible(true);
      } catch (e: any) {
        const { showAuthBanner } = classifyWearableError(e);
        if (!cancelled) {
          if (showAuthBanner) setAuthMissing(true);
          setVisible(true);
        }
      }

    })();
    return () => {
      cancelled = true;
    };
  }, [list]);

  if (!visible) return null;

  if (authMissing) {
    return (
      <div style={{ marginBottom: 14 }}>
        <SignInForWearablesBanner />
      </div>
    );
  }

  const isTestUser = getIsTestUser();

  if (!isTestUser) {
    return (
      <div
        style={{
          background: GL,
          border: `1px solid ${TL}`,
          borderRadius: 16,
          padding: 14,
          marginBottom: 14,
          display: "flex",
          gap: 12,
          alignItems: "center",
          opacity: 0.85,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: GR, marginBottom: 2 }}>
            Wearable sync — coming soon
          </div>
          <div style={{ fontSize: 12, color: GR, lineHeight: 1.5 }}>
            Auto-import sleep, HRV, resting heart rate and steps from Fitbit, Oura,
            Garmin or Whoop.
          </div>
        </div>
      </div>
    );
  }

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    setVisible(false);
  };

  return (
    <div
      style={{
        background: WH,
        border: `1px solid ${TL}`,
        borderRadius: 16,
        padding: 14,
        marginBottom: 14,
        display: "flex",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: PL, marginBottom: 2 }}>
          Connect a wearable
        </div>
        <div style={{ fontSize: 12, color: GR, lineHeight: 1.5 }}>
          Auto-import sleep, HRV, resting heart rate and steps from Fitbit, Oura,
          Garmin or Whoop.
        </div>
      </div>
      <button
        onClick={() => {
          dismiss();
          onOpenSettings();
        }}
        style={{
          background: PL,
          color: WH,
          border: "none",
          borderRadius: 10,
          padding: "8px 12px",
          fontSize: 12.5,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Connect
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent",
          color: GR,
          border: "none",
          fontSize: 18,
          cursor: "pointer",
          padding: 4,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

export default WearableBanner;
